import { computed, ref, shallowRef } from "vue";
import { getWallet, getWalletTransactions, cancelOrder, cancelOrderLineItem } from "@/core/api/graphql/wallet";
import { Logger } from "@/core/utilities";
import type {
  WalletType,
  WalletTransactionType,
  OrderCancellationResultType,
} from "@/core/api/graphql/wallet/types";
import type { GetWalletTransactionsQueryVariables } from "@/core/api/graphql/wallet/queries/getWalletTransactions";

const loading = ref(false);
const wallet = shallowRef<WalletType | null>(null);
const transactions = shallowRef<WalletTransactionType[]>([]);
const transactionsTotalCount = ref(0);
const currentPage = ref(1);
const pageSize = ref(10);

export function useWallet() {
  async function fetchWallet(customerId: string) {
    loading.value = true;

    try {
      wallet.value = await getWallet({ customerId });
    } catch (e) {
      Logger.error(`${useWallet.name}.fetchWallet`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function fetchTransactions(payload: GetWalletTransactionsQueryVariables) {
    loading.value = true;

    try {
      const result = await getWalletTransactions(payload);
      transactions.value = result.items;
      transactionsTotalCount.value = result.totalCount;
    } catch (e) {
      Logger.error(`${useWallet.name}.fetchTransactions`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function loadMoreTransactions(walletId: string) {
    if (!walletId) return;

    const skip = currentPage.value * pageSize.value;
    
    loading.value = true;
    try {
      const result = await getWalletTransactions({
        walletId,
        skip,
        take: pageSize.value,
      });

      transactions.value = [...transactions.value, ...result.items];
      currentPage.value += 1;
    } catch (e) {
      Logger.error(`${useWallet.name}.loadMoreTransactions`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function cancelUserOrder(orderId: string, cancelReason?: string): Promise<OrderCancellationResultType> {
    loading.value = true;

    try {
      const result = await cancelOrder(orderId, cancelReason);
      return result;
    } catch (e) {
      Logger.error(`${useWallet.name}.cancelUserOrder`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function cancelUserOrderLineItem(
    orderId: string,
    lineItemId: string,
    cancelReason?: string,
  ): Promise<OrderCancellationResultType> {
    loading.value = true;

    try {
      const result = await cancelOrderLineItem(orderId, lineItemId, cancelReason);
      return result;
    } catch (e) {
      Logger.error(`${useWallet.name}.cancelUserOrderLineItem`, e);
      throw e;
    } finally {
      loading.value = false;
    }
  }

  function clearWallet() {
    wallet.value = null;
    transactions.value = [];
    transactionsTotalCount.value = 0;
    currentPage.value = 1;
  }

  const hasMoreTransactions = computed(() => {
    return transactions.value.length < transactionsTotalCount.value;
  });

  return {
    loading: computed(() => loading.value),
    wallet: computed(() => wallet.value),
    transactions: computed(() => transactions.value),
    transactionsTotalCount: computed(() => transactionsTotalCount.value),
    hasMoreTransactions,
    currentPage,
    pageSize,
    fetchWallet,
    fetchTransactions,
    loadMoreTransactions,
    cancelUserOrder,
    cancelUserOrderLineItem,
    clearWallet,
  };
}
